/* =========================================================================
   Blatantly British Dynamic Collections — Horizon v3.2

   Purpose
   -------
   When a Meta catalogue ad sends a customer to a collection URL with:
       ?first=<product handle or product URL>

   this script renders that exact product using the store's native Horizon
   _product-card block and moves it into product slot #1.

   No hard-coded page size is required. The script preserves however many
   product cards the collection currently rendered.

   Examples
   --------
   /collections/the-uks-finest?first=the-catfather

   /collections/the-uks-finest?first=https%3A%2F%2Fblatantlybritish.co.uk%2Fproducts%2Fthe-catfather

   Safe failure behaviour
   ----------------------
   The live collection is not changed until the requested product card has
   been successfully rendered by Shopify.
   ========================================================================= */

(() => {
  'use strict';

  const ITEM_SEL = '.product-grid__item[data-product-id]';
  const SMALL_SIZE_VALUES = new Set(['s', 'small']);
  const ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  if (!/\/collections\//.test(window.location.pathname)) return;

  const params = new URLSearchParams(window.location.search);

  // Preserve the original dynamic landing state. Horizon can rewrite the
  // browser URL during AJAX sorting/filtering and may omit unknown parameters.
  // We restore these values after each results-list update so a refresh keeps
  // the advertised product pinned.
  const preservedFirst = params.get('first') || '';
  const preservedOuterVariant = params.get('variant') || '';

  let raw = preservedFirst.trim();
  if (!raw) return;

  // URLSearchParams already decodes once. This handles an extra encoding layer.
  try {
    if (/%[0-9A-Fa-f]{2}/.test(raw)) raw = decodeURIComponent(raw);
  } catch (_) {}

  let handle = '';
  let variantId = '';

  if (/^https?:\/\//i.test(raw)) {
    try {
      const productUrl = new URL(raw, window.location.origin);
      const parts = productUrl.pathname.split('/').filter(Boolean);
      const productIndex = parts.indexOf('products');

      if (productIndex !== -1 && parts[productIndex + 1]) {
        handle = parts[productIndex + 1];
      }

      variantId = productUrl.searchParams.get('variant') || '';
    } catch (_) {
      return;
    }
  } else {
    const qIndex = raw.indexOf('?');
    const rawPath = qIndex === -1 ? raw : raw.slice(0, qIndex);
    const rawQuery = qIndex === -1 ? '' : raw.slice(qIndex + 1);

    handle = rawPath.split('/').filter(Boolean).pop() || '';

    if (rawQuery) {
      try {
        variantId = new URLSearchParams(rawQuery).get('variant') || '';
      } catch (_) {}
    }
  }

  // If Meta inserts an unencoded product URL, ?variant= can become an outer
  // parameter. Treat that as a valid fallback.
  const outerVariant = params.get('variant') || '';
  if (outerVariant) variantId = outerVariant;

  handle = handle.replace(/[^a-z0-9_-]/gi, '');
  if (!/^\d+$/.test(variantId)) variantId = '';
  if (!handle) return;

  const stateKey = `${handle}|${variantId}`;

  function ensureDynamicParamsInAddressBar() {
    try {
      const current = new URL(window.location.href);
      if (!/\/collections\//.test(current.pathname)) return;

      let changed = false;

      if (current.searchParams.get('first') !== preservedFirst) {
        current.searchParams.set('first', preservedFirst);
        changed = true;
      }

      if (
        preservedOuterVariant &&
        current.searchParams.get('variant') !== preservedOuterVariant
      ) {
        current.searchParams.set('variant', preservedOuterVariant);
        changed = true;
      }

      if (changed) {
        const next =
          current.pathname + current.search + current.hash;
        window.history.replaceState(window.history.state, '', next);
      }
    } catch (_) {}
  }

  function directProductItems(grid) {
    return Array.from(grid.children).filter(
      (el) => el instanceof Element && el.matches(ITEM_SEL)
    );
  }

  function findGrid(root = document) {
    const firstItem = root.querySelector(`results-list ${ITEM_SEL}, ${ITEM_SEL}`);
    return firstItem ? firstItem.parentElement : null;
  }

  function updatePositionClasses(grid) {
    directProductItems(grid).forEach((item, index) => {
      Array.from(item.classList).forEach((className) => {
        if (/^product-grid__item--\d+$/.test(className)) {
          item.classList.remove(className);
        }
      });
      item.classList.add(`product-grid__item--${index}`);
    });
  }

  function setVariantLink(card, meta) {
    if (!meta || !meta.url) return;

    const mainLink = card.querySelector('a.product-card__link');
    if (mainLink) {
      mainLink.setAttribute('href', meta.url);
    }
  }

  function normaliseOptionValue(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function variantOptions(variant) {
    if (!variant) return [];

    if (Array.isArray(variant.options)) {
      return variant.options.map((value) =>
        String(value == null ? '' : value)
      );
    }

    return [variant.option1, variant.option2, variant.option3].map((value) =>
      String(value == null ? '' : value)
    );
  }

  function sizeOptionIndex(productData) {
    if (!productData || !Array.isArray(productData.options)) return -1;

    return productData.options.findIndex((option) => {
      const name =
        typeof option === 'string'
          ? option
          : option && typeof option.name === 'string'
            ? option.name
            : '';

      return /(^|\b)size(\b|$)/i.test(name);
    });
  }

  let resolvedVariantPromise = null;

  async function resolveSmallVariantId() {
    if (resolvedVariantPromise) return resolvedVariantPromise;

    resolvedVariantPromise = (async () => {
      try {
        const response = await fetch(
          `${ROOT}products/${encodeURIComponent(handle)}.js`,
          {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          }
        );

        if (!response.ok) {
          throw new Error(`Product data fetch failed (${response.status})`);
        }

        const productData = await response.json();
        const variants = Array.isArray(productData.variants)
          ? productData.variants
          : [];
        const sizeIndex = sizeOptionIndex(productData);

        // Products without a Size option keep Meta's exact variant behaviour.
        if (sizeIndex < 0 || !variants.length) return variantId;

        const smallVariants = variants.filter((variant) => {
          const options = variantOptions(variant);
          return SMALL_SIZE_VALUES.has(normaliseOptionValue(options[sizeIndex]));
        });

        // If this product has no S/Small variant, retain the original variant.
        if (!smallVariants.length) return variantId;

        const sourceVariant = variants.find(
          (variant) => String(variant.id) === String(variantId)
        );
        const sourceOptions = variantOptions(sourceVariant);

        // Preserve colour and any other options from the advertised variant.
        // Only the Size option is normalised to S/Small.
        const sameOtherOptions = sourceVariant
          ? smallVariants.filter((candidate) => {
              const candidateOptions = variantOptions(candidate);

              return sourceOptions.every((sourceValue, index) => {
                if (index === sizeIndex) return true;
                return (
                  normaliseOptionValue(candidateOptions[index]) ===
                  normaliseOptionValue(sourceValue)
                );
              });
            })
          : [];

        const chosen =
          sameOtherOptions.find((candidate) => candidate.available !== false) ||
          sameOtherOptions[0] ||
          smallVariants.find((candidate) => candidate.available !== false) ||
          smallVariants[0];

        return chosen && chosen.id != null ? String(chosen.id) : variantId;
      } catch (error) {
        console.warn(
          '[BB Dynamic Collections] Could not normalise ad variant to Small.',
          error
        );

        // Do not deliberately force an XL/2XL ad variant if product-data
        // resolution fails. Let Shopify render its normal default variant.
        return '';
      }
    })();

    return resolvedVariantPromise;
  }

  async function fetchPinnedCard() {
    const renderVariantId = await resolveSmallVariantId();
    const url =
      `${ROOT}products/${encodeURIComponent(handle)}` +
      `?view=bb-card` +
      (renderVariantId
        ? `&variant=${encodeURIComponent(renderVariantId)}`
        : '');

    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      throw new Error(`Product card render failed (${response.status})`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const card = doc.querySelector(ITEM_SEL);

    if (!card) {
      throw new Error(
        'Shopify returned no Horizon product card. Check templates/product.bb-card.json and sections/bb-dynamic-product-card.liquid.'
      );
    }

    let meta = null;
    const metaEl = doc.querySelector('script[data-bb-curated-meta]');
    if (metaEl) {
      try {
        meta = JSON.parse(metaEl.textContent);
      } catch (_) {}
    }

    setVariantLink(card, meta);

    card.classList.add('curated-first-item');
    card.dataset.curatedHandle = handle;
    card.dataset.curatedState = stateKey;

    return { card, meta };
  }

  let busy = false;
  let scheduled = false;

  async function pinProduct() {
    ensureDynamicParamsInAddressBar();

    const grid = findGrid();
    if (!grid || busy) return;

    const currentFirstProduct = directProductItems(grid)[0];

    if (
      currentFirstProduct &&
      currentFirstProduct.classList.contains('curated-first-item') &&
      currentFirstProduct.dataset.curatedState === stateKey
    ) {
      return;
    }

    const originalProductCount = directProductItems(grid).length;
    if (!originalProductCount) return;

    busy = true;

    try {
      // Fetch first. If this fails, the live collection remains untouched.
      const { card, meta } = await fetchPinnedCard();
      const liveGrid = findGrid();
      if (!liveGrid) return;

      const productId =
        (meta && meta.product_id != null ? String(meta.product_id) : '') ||
        card.getAttribute('data-product-id') ||
        '';

      // Remove a previously pinned card.
      directProductItems(liveGrid).forEach((item) => {
        if (item.classList.contains('curated-first-item')) {
          item.remove();
        }
      });

      // Remove the ordinary copy if this product is already on the current page.
      if (productId) {
        directProductItems(liveGrid).forEach((item) => {
          if (item.getAttribute('data-product-id') === productId) {
            item.remove();
          }
        });
      }

      liveGrid.prepend(card);

      // Preserve the number of product cards that were visible before pinning.
      let items = directProductItems(liveGrid);
      while (items.length > originalProductCount) {
        const last = items[items.length - 1];
        if (last === card && items.length > 1) {
          items[items.length - 2].remove();
        } else {
          last.remove();
        }
        items = directProductItems(liveGrid);
      }

      updatePositionClasses(liveGrid);
      liveGrid.dataset.bbDynamicCollection = stateKey;
    } catch (error) {
      console.warn('[BB Dynamic Collections]', error);
    } finally {
      busy = false;
    }
  }

  function schedulePin() {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      pinProduct();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pinProduct, { once: true });
  } else {
    pinProduct();
  }

  // Horizon's results-list can rebuild the collection after sorting/filtering.
  // If the grid is replaced, restore the advertised product to slot #1.
  const observer = new MutationObserver(() => {
    // Horizon may update the address bar as part of the same AJAX refresh.
    // Repair the URL immediately, then once more after its update settles.
    ensureDynamicParamsInAddressBar();
    window.setTimeout(ensureDynamicParamsInAddressBar, 150);

    if (busy) return;

    const grid = findGrid();
    if (!grid) return;

    const firstProduct = directProductItems(grid)[0];
    const correct =
      firstProduct &&
      firstProduct.classList.contains('curated-first-item') &&
      firstProduct.dataset.curatedState === stateKey;

    if (!correct) schedulePin();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  window.addEventListener('popstate', () => {
    window.setTimeout(() => {
      ensureDynamicParamsInAddressBar();
      schedulePin();
    }, 0);
  });
})();
